import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IoMdClose } from 'react-icons/io';

import showNotification from 'utils/showNotification';
import { useEvieAppContext } from '../EvieAppProvider';
import { useMarkContentAsCompleted } from '../Service/useMarkContentAsCompleted';

import './capsuleView.css';

const CapsuleView = ({ isAssistantView, changeViewStatus, selectContent, selectedContent }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const videoRef = useRef(null);
  const { refetch, activeProgram = {}, completedActivities = [] } = isAssistantView ? {} : useEvieAppContext();
  const { markContentCompleted } = useMarkContentAsCompleted();
  const patientProgramId = activeProgram.id;
  const {
    title,
    file: videoUrl,
    fileUs: videoUrlUs,
    mediaUrl = '',
    backgroundImage: { original = '' } = {},
    id: contentId,
    contentType,
  } = selectedContent;

  const videoToUse = mediaUrl || (language === 'en-US' ? videoUrlUs : videoUrl);
  const isAlreadyCompleted = completedActivities.some((activity) => activity.id === contentId);

  const handleVideoEnd = async () => {
    setTimeout(() => {
      changeViewStatus();
      selectContent(null);
    }, 2000);

    if (isAlreadyCompleted || isAssistantView) return;
    try {
      const result = await markContentCompleted({
        contentId,
        contentType: contentType || 'short_video',
        patientProgramId,
      });

      if (result.success) {
        await refetch();
        showNotification({
          type: 'success',
          messages: [t('Video completed successfully!')],
        });
      } else {
        showNotification({
          type: 'error',
          messages: [result.error || t('Error completing video')],
        });
      }
    } catch (error) {
      showNotification({
        type: 'error',
        messages: [t('Error completing video')],
      });
    }
  };

  const handleCloseView = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = '';
    }
    changeViewStatus();
    selectContent(null);
  };

  return (
    <div className="capsule-view">
      <div className="capsule-container">
        <div className="top-bar">
          <IoMdClose className="capsule-close-btn" onClick={handleCloseView} />
          <h2 className="capsule-title">{title}</h2>
        </div>
      </div>
      <div className="video-container">
        <video
          ref={videoRef}
          className="video-player"
          src={videoToUse}
          poster={original}
          controls
          autoPlay
          playsInline
          onEnded={handleVideoEnd}
        >
          <track kind="captions" />
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  );
};

CapsuleView.defaultProps = {
  isAssistantView: false,
};

CapsuleView.propTypes = {
  isAssistantView: PropTypes.bool,
  changeViewStatus: PropTypes.func.isRequired,
  selectContent: PropTypes.func.isRequired,
  selectedContent: PropTypes.shape({
    __typename: PropTypes.string,
    contentType: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    file: PropTypes.string,
    fileUs: PropTypes.string,
    mediaUrl: PropTypes.string,
    backgroundImage: PropTypes.shape({
      original: PropTypes.string,
    }),
  }).isRequired,
};

export default CapsuleView;
